import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SERVER = process.env.REACT_APP_SERVER_URL || '';
const EMULATOR_DATA = 'https://cdn.emulatorjs.org/stable/data/';
const EMULATOR_LOADER = `${EMULATOR_DATA}loader.js`;
const EMULATOR_ASSIGN_TIMEOUT = 30000;
const EMULATOR_EXIT_GRACE = 1200;

// EmulatorJS exposes one global instance and its loader also uses global EJS_*
// configuration. Keep mounts serialized so an Exit/refresh cannot overlap the
// next loader and leave an old audio/runtime instance alive.
let emulatorLifecycle = Promise.resolve();

function queueEmulatorTask(task) {
  const next = emulatorLifecycle.then(task, task);
  emulatorLifecycle = next.catch(() => {});
  return next;
}

const waitForEmulator = ms => new Promise(resolve => window.setTimeout(resolve, ms));

function stopEmulatorAudio(emulator) {
  const audio = emulator?.Module?.AL?.currentCtx;
  const sources = audio?.sources;
  if (sources) {
    Object.values(sources).forEach(source => {
      [source?.source, source?.gain, source?.panner].forEach(node => {
        try { node?.stop?.(); } catch {}
        try { node?.disconnect?.(); } catch {}
      });
    });
  }
  try { audio?.audioCtx?.close?.(); } catch {}
}

const SYSTEMS = {
  nes: { label: 'NES', core: 'nes', extensions: ['.nes', '.fds'] },
  snes: { label: 'SNES', core: 'snes', extensions: ['.smc', '.sfc', '.swc'] },
  segaMD: { label: 'Genesis', core: 'segaMD', extensions: ['.gen', '.md', '.bin', '.smd'] },
};
const ROM_ACCEPT = [...new Set(Object.values(SYSTEMS).flatMap(system => [...system.extensions, '.zip']))].join(',');

function fileExtension(name) {
  const match = String(name || '').toLowerCase().match(/\.[a-z0-9]+$/);
  return match ? match[0] : '';
}

function findZipEnd(bytes) {
  const minimumOffset = Math.max(0, bytes.length - 0x10000 - 22);
  for (let offset = bytes.length - 22; offset >= minimumOffset; offset -= 1) {
    if (bytes[offset] === 0x50 && bytes[offset + 1] === 0x4b && bytes[offset + 2] === 0x05 && bytes[offset + 3] === 0x06) return offset;
  }
  return -1;
}

async function readZipEntryNames(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const end = findZipEnd(bytes);
  if (end < 0 || end + 22 > view.byteLength) return [];
  const entryCount = view.getUint16(end + 10, true);
  const directorySize = view.getUint32(end + 12, true);
  const directoryOffset = view.getUint32(end + 16, true);
  const decoder = new TextDecoder();
  const names = [];
  let offset = directoryOffset;
  for (let index = 0; index < entryCount && offset + 46 <= view.byteLength; index += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const nameStart = offset + 46;
    const nameEnd = nameStart + nameLength;
    if (nameEnd > view.byteLength) break;
    names.push(decoder.decode(bytes.slice(nameStart, nameEnd)));
    offset = nameEnd + extraLength + commentLength;
  }
  return directorySize ? names : [];
}

async function detectRomSystem(file) {
  const extension = fileExtension(file.name);
  if (extension !== '.zip') {
    const match = Object.entries(SYSTEMS).find(([, system]) => system.extensions.includes(extension));
    if (!match) throw new Error('Use an NES, SNES, or Genesis ROM, or a ZIP containing one.');
    return match[0];
  }

  const names = await readZipEntryNames(file);
  const matches = Object.entries(SYSTEMS).filter(([, system]) => names.some(name => {
    const entryExtension = fileExtension(name.split('/').pop());
    return system.extensions.includes(entryExtension);
  }));
  if (matches.length === 1) return matches[0][0];
  if (matches.length > 1) throw new Error('That ZIP contains multiple console systems. Upload one ZIP containing only the NES, SNES, or Genesis game.');
  throw new Error('I could not find a supported ROM inside that ZIP. Include a .nes, .sfc/.smc, or .gen/.md ROM.');
}

// EmulatorJS uses the libretro controller order: B, Y, Select, Start,
// Up, Down, Left, Right, A, X, L, R.
const BUTTONS = {
  0: 'B', 1: 'Y', 2: 'Select', 3: 'Start', 4: 'Up', 5: 'Down',
  6: 'Left', 7: 'Right', 8: 'A', 9: 'X', 10: 'L', 11: 'R',
};

const KEYMAPS = {
  1: { ArrowUp: 4, ArrowDown: 5, ArrowLeft: 6, ArrowRight: 7, KeyZ: 0, KeyX: 8, KeyA: 1, KeyS: 9, ShiftLeft: 2, Enter: 3, KeyQ: 10, KeyW: 11 },
  2: { KeyW: 4, KeyS: 5, KeyA: 6, KeyD: 7, KeyG: 0, KeyH: 8, KeyT: 1, KeyY: 9, KeyF: 2, KeyR: 3, KeyQ: 10, KeyE: 11 },
};

const C = {
  bg: '#0E0E10', panel: '#18181B', card: '#1F1F23', border: '#2A2A2E',
  text: '#EFEFF1', muted: '#ADADB8', accent: '#9147FF', gold: '#C8922A',
  green: '#22C55E', red: '#EF4444', input: '#3A3A3D',
};

function GameCanvas({ game, onReady, onError, onStreamReady, pendingInputs, isMobile, destroyRef, captureStream = false, silent = false }) {
  const containerRef = useRef(null);
  const generationRef = useRef(0);
  const [containerId] = useState(() => `tavern-game-${Math.random().toString(36).slice(2)}`);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!game) return undefined;
    const generation = ++generationRef.current;
    const record = {
      cancelled: false,
      emulator: null,
      stoppedEmulator: null,
      script: null,
      mediaStream: null,
      startTimer: null,
      previousStart: window.EJS_onGameStart,
      onStart: null,
      teardownQueued: false,
    };
    let startTimer;
    let started = false;
    const applyPending = () => {
      const emulator = record.emulator || window.EJS_emulator;
      const simulate = emulator?.gameManager?.simulateInput;
      if (!simulate) return;
      pendingInputs.current.forEach((value, key) => {
        const [controller, button] = key.split(':').map(Number);
        simulate(controller - 1, button, value);
      });
    };

    const stopEmulator = () => {
      const emulator = record.emulator || window.EJS_emulator;
      if (!emulator || record.stoppedEmulator === emulator) return Boolean(record.stoppedEmulator);
      record.emulator = emulator;
      record.stoppedEmulator = emulator;
      try { emulator.gameManager?.toggleMainLoop?.(0); } catch {}
      try { emulator.callEvent?.('exit'); } catch {}
      stopEmulatorAudio(emulator);
      return true;
    };

    const stopMediaStream = () => {
      record.mediaStream?.getTracks?.().forEach(track => track.stop());
      record.mediaStream = null;
      onStreamReady?.(null);
    };

    const removeMount = () => {
      record.script?.remove();
      if (containerRef.current) containerRef.current.replaceChildren();
      if (window.EJS_emulator === record.emulator || (!record.emulator && window.EJS_emulator)) {
        delete window.EJS_emulator;
      }
      if (window.EJS_onGameStart === record.onStart) window.EJS_onGameStart = record.previousStart;
      if (window.EJS_player === `#${containerId}`) {
        ['EJS_player', 'EJS_core', 'EJS_gameUrl', 'EJS_gameName', 'EJS_gameID', 'EJS_startOnLoaded'].forEach(key => delete window[key]);
      }
    };

    const finalizeTeardown = () => queueEmulatorTask(async () => {
      if (record.teardownQueued) return;
      record.teardownQueued = true;
      removeMount();
      if (record.stoppedEmulator) await waitForEmulator(EMULATOR_EXIT_GRACE);
    });

    const requestDestroy = () => {
      if (record.cancelled) return;
      record.cancelled = true;
      window.clearTimeout(startTimer);
      record.startTimer = null;
      stopMediaStream();
      stopEmulator();
      removeMount();
      setReady(false);
      onReady(false);
      finalizeTeardown();
    };

    const waitForAssignedEmulator = async () => {
      const deadline = Date.now() + EMULATOR_ASSIGN_TIMEOUT;
      while (Date.now() < deadline) {
        if (window.EJS_emulator) {
          record.emulator = window.EJS_emulator;
          if (record.cancelled) stopEmulator();
          return record.emulator;
        }
        await waitForEmulator(50);
      }
      return null;
    };

    setReady(false);
    onReady(false);
    if (containerRef.current) containerRef.current.innerHTML = '';

    if (destroyRef) destroyRef.current = requestDestroy;

    queueEmulatorTask(async () => {
      if (record.cancelled) return;

      // Recover from an instance left behind by an older page build or an
      // interrupted loader before configuring the singleton for this mount.
      if (window.EJS_emulator) {
        record.emulator = window.EJS_emulator;
        stopEmulator();
        if (window.EJS_emulator === record.emulator) delete window.EJS_emulator;
        await waitForEmulator(EMULATOR_EXIT_GRACE);
        record.emulator = null;
        record.stoppedEmulator = null;
      }

      window.EJS_player = `#${containerId}`;
      window.EJS_core = game.core || SYSTEMS[game.system]?.core;
      window.EJS_gameUrl = `${SERVER}${game.romUrl}`;
      window.EJS_gameName = game.name;
      window.EJS_gameID = Math.abs([...String(game.gameId)].reduce((hash, char) => ((hash << 5) - hash) + char.charCodeAt(0), 0)) || 1;
      window.EJS_pathtodata = EMULATOR_DATA;
      window.EJS_startOnLoaded = true;
      window.EJS_noAutoFocus = true;
      window.EJS_volume = silent ? 0 : 1;
      window.EJS_browserMode = isMobile ? 'mobile' : 'desktop';
      window.EJS_threads = Boolean(
        isMobile &&
        window.crossOriginIsolated &&
        typeof window.SharedArrayBuffer === 'function'
      );
      window.EJS_disableAutoLang = true;
      window.EJS_defaultOptions = { ...(window.EJS_defaultOptions || {}), keyboardInput: 'disabled' };
      record.onStart = () => {
        if (record.cancelled || generation !== generationRef.current) return;
        started = true;
        window.clearTimeout(startTimer);
        setReady(true);
        onReady(true);
        if (captureStream) {
          const captureCanvas = record.emulator?.canvas || containerRef.current?.querySelector('canvas');
          try {
            const stream = record.emulator?.collectScreenRecordingMediaTracks?.(captureCanvas, 60);
            if (stream?.getTracks?.().length) {
              record.mediaStream = stream;
              onStreamReady?.(stream);
            } else {
              onStreamReady?.(null);
            }
          } catch {
            onStreamReady?.(null);
          }
        }
        applyPending();
        if (typeof record.previousStart === 'function') record.previousStart();
      };
      window.EJS_onGameStart = record.onStart;

      record.script = document.createElement('script');
      record.script.src = EMULATOR_LOADER;
      record.script.async = true;
      record.script.onerror = () => {
        if (!record.cancelled && generation === generationRef.current) {
          onError('The emulator could not load. Check the ROM and try again.');
          requestDestroy();
        }
      };
      document.body.appendChild(record.script);
      startTimer = window.setTimeout(() => {
        if (!record.cancelled && generation === generationRef.current && !started) {
          onError('The emulator did not start. Check that the ZIP contains one supported NES, SNES, or Genesis ROM.');
          requestDestroy();
        }
      }, 45000);
      record.startTimer = startTimer;

      const emulator = await waitForAssignedEmulator();
      if (!emulator && !record.cancelled && generation === generationRef.current) {
        onError('The emulator loader did not start. Check the ROM and try again.');
        requestDestroy();
      }
    });

    return () => {
      generationRef.current += 1;
      if (destroyRef?.current === requestDestroy) destroyRef.current = null;
      requestDestroy();
    };
  }, [game?.gameId, game?.romUrl, game?.system, game?.core, game?.name, containerId, onError, onReady, onStreamReady, pendingInputs, destroyRef, captureStream, silent]);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 'calc(100vw - 28px)', aspectRatio: '4 / 3', boxSizing: 'border-box', background: '#000', overflow: 'hidden', margin: '0 auto' }}>
      {!ready && <div style={{ position: 'absolute', inset: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 14, background: '#050505' }}>Loading {game.name}…</div>}
      <div ref={containerRef} id={containerId} style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
    </div>
  );
}

function WakeLockButton() {
  const [held, setHeld] = useState(false);
  const lockRef = useRef(null);
  const requestedRef = useRef(false);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  const acquire = useCallback(async () => {
    if (!supported) return;
    requestedRef.current = true;
    try {
      lockRef.current?.release?.();
      const lock = await navigator.wakeLock.request('screen');
      lockRef.current = lock;
      setHeld(true);
      lock.addEventListener?.('release', () => {
        if (lockRef.current === lock) {
          lockRef.current = null;
          setHeld(false);
        }
      });
    } catch {}
  }, [supported]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && requestedRef.current) acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      requestedRef.current = false;
      lockRef.current?.release?.();
      lockRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [acquire]);

  if (!supported) return null;
  return <button onClick={acquire} aria-label={held ? 'Screen stay-awake enabled' : 'Keep screen awake'} style={{ background: '#000b', color: held ? C.green : C.text, border: `1px solid ${held ? C.green : C.border}`, borderRadius: 7, padding: '6px 9px', fontSize: 11, cursor: 'pointer' }}>{held ? '☀ Awake' : '☼ Keep awake'}</button>;
}

function GameVideo({ stream, onReady, active = true }) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().then(() => onReady?.(true)).catch(() => {});
  }, [onReady]);
  const fullscreen = useCallback(() => {
    const video = videoRef.current;
    const player = playerRef.current;
    try {
      if (video?.requestFullscreen) return video.requestFullscreen();
      if (video?.webkitEnterFullscreen) return video.webkitEnterFullscreen();
      if (player?.requestFullscreen) return player.requestFullscreen();
    } catch {}
    return undefined;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    video.srcObject = active ? (stream || null) : null;
    if (!active || !stream) {
      if (active) onReady?.(false);
      return undefined;
    }
    onReady?.(true);
    video.onloadedmetadata = play;
    play();
    return () => {
      video.onloadedmetadata = null;
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [stream, active, play, onReady]);

  return (
    <div ref={playerRef} style={{ position: 'relative', width: '100%', maxWidth: 'calc(100vw - 28px)', aspectRatio: '4 / 3', boxSizing: 'border-box', background: '#000', overflow: 'hidden', margin: '0 auto' }}>
      {!stream && <div style={{ position: 'absolute', inset: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 14 }}>Connecting to the host’s game screen…</div>}
      <video ref={videoRef} autoPlay playsInline onClick={play} onDoubleClick={fullscreen} style={{ display: 'block', width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', background: '#000' }} />
      {stream && <div style={{ position: 'absolute', right: 10, bottom: 10, display: 'flex', gap: 6, zIndex: 2 }}>
        <button onClick={play} aria-label="Enable audio" style={{ background: '#000b', color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 9px', fontSize: 11, cursor: 'pointer' }}>Enable audio</button>
        <WakeLockButton />
        <button onClick={fullscreen} aria-label="Fullscreen game" style={{ background: '#000b', color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 9px', fontSize: 14, lineHeight: 1, cursor: 'pointer' }}>⛶</button>
      </div>}
    </div>
  );
}

function ControllerPad({ controller, active, onPress }) {
  const layout = [
    [4, null, 0, 8, null],
    [6, 5, 7, 2, 3],
  ];
  const buttonStyle = (button) => ({
    width: button === null ? 38 : 42,
    height: button === null ? 38 : 38,
    borderRadius: button === null ? 12 : 9,
    border: `1px solid ${button === null ? 'transparent' : C.border}`,
    background: button === null ? 'transparent' : (button === 0 || button === 8 ? '#9147FF33' : C.card),
    color: button === null ? 'transparent' : C.text,
    fontSize: button === null ? 1 : 10,
    fontWeight: 800,
    cursor: active && button !== null ? 'pointer' : 'not-allowed',
    touchAction: 'none',
    opacity: active && button !== null ? 1 : 0.42,
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
      <div style={{ color: controller === 1 ? C.accent : C.green, fontSize: 11, fontWeight: 800 }}>P{controller} CONTROLS</div>
      {layout.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: 5 }}>
          {row.map((button, index) => (
            <button key={`${rowIndex}-${index}`} disabled={!active || button === null} style={buttonStyle(button)}
              onPointerDown={e => { e.preventDefault(); if (button !== null) onPress(button, true); }}
              onPointerUp={e => { e.preventDefault(); if (button !== null) onPress(button, false); }}
              onPointerCancel={() => button !== null && onPress(button, false)}
              onPointerLeave={() => button !== null && onPress(button, false)}>
              {button === null ? '' : BUTTONS[button]}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function GameRoom({ socket, displayName, isMobile = false }) {
  const [state, setState] = useState({ game: null, hostId: null, members: [], controllers: [], streamReady: false });
  const [system, setSystem] = useState('nes');
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState('');
  const [emulatorReady, setEmulatorReady] = useState(false);
  const [emulatorError, setEmulatorError] = useState('');
  const [localGameStream, setLocalGameStream] = useState(null);
  const [remoteGameStream, setRemoteGameStream] = useState(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [networkStats, setNetworkStats] = useState({ rttMs: null, inputAckMs: null, playerDeliveryMs: null, transport: 'connecting', frame: null, fps: null, audioState: 'unknown', audioSampleRate: null, receivedInputs: 0, serverInputs: 0, playerForwards: 0, spectatorForwards: 0, playerDeliveryFailures: 0 });
  const pendingInputs = useRef(new Map());
  const inputStats = useRef({ sent: 0, received: 0, nextId: 0, inputAckMs: null, playerDeliveryMs: null });
  const frameStats = useRef({ frame: null, at: 0, fps: null });
  const gameRef = useRef(null);
  const seatRef = useRef(null);
  const socketRef = useRef(socket);
  const emulatorDestroyRef = useRef(null);
  const localGameStreamRef = useRef(null);
  const gameStreamPeersRef = useRef(new Map());
  const gameStreamIceRef = useRef(new Map());
  const sourceState = useRef({ keyboard: new Set(), gamepad: new Set(), pointer: new Set() });

  const myMember = useMemo(() => state.members.find(member => member.socketId === socket?.id) || state.members.find(member => member.name === displayName), [state.members, socket, displayName]);
  const mySeat = myMember?.controller || null;
  const isHost = Boolean(socket && state.hostId === socket.id);
  // Loading is available to any connected game-room participant. The server
  // still requires the socket to have joined the room before accepting it.
  const canLoadGame = Boolean(socket);
  const controllerSeats = state.controllers.length ? state.controllers : [1, 2].map(controller => ({ controller, socketId: null, name: null }));

  useEffect(() => { gameRef.current = state.game; }, [state.game]);
  useEffect(() => { seatRef.current = mySeat; }, [mySeat]);
  useEffect(() => { socketRef.current = socket; }, [socket]);

  const handleLocalGameStream = useCallback(stream => {
    localGameStreamRef.current = stream;
    setLocalGameStream(stream);
  }, []);

  const handleCanvasReady = useCallback(ready => {
    setEmulatorReady(ready);
  }, []);

  useEffect(() => {
    const gameId = state.game?.gameId;
    if (!socket || !gameId || !isHost) return undefined;
    socket.emit('game_stream_status', { gameId, ready: Boolean(localGameStream) });
    return () => {
      if (socket.connected) socket.emit('game_stream_status', { gameId, ready: false });
    };
  }, [socket, state.game?.gameId, isHost, isHost ? localGameStream : null]);

  useEffect(() => {
    const gameId = state.game?.gameId;
    if (!socket || !gameId) return undefined;
    let active = true;
    const peers = gameStreamPeersRef.current;
    const iceQueue = gameStreamIceRef.current;
    const closePeer = peerId => {
      peers.get(peerId)?.close();
      peers.delete(peerId);
      iceQueue.delete(peerId);
    };
    const closeAll = () => [...peers.keys()].forEach(closePeer);
    const turnServers = () => fetch(`${SERVER}/turn-credentials`).then(response => response.json()).catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));
    const addIce = async (peerId, candidate) => {
      const pc = peers.get(peerId);
      if (!pc || !candidate) return;
      if (!pc.remoteDescription) {
        const queued = iceQueue.get(peerId) || [];
        queued.push(candidate);
        iceQueue.set(peerId, queued);
        return;
      }
      try { await pc.addIceCandidate(candidate); } catch {}
    };
    const drainIce = async peerId => {
      const queued = iceQueue.get(peerId) || [];
      iceQueue.delete(peerId);
      for (const candidate of queued) {
        try { await peers.get(peerId)?.addIceCandidate(candidate); } catch {}
      }
    };
    const createHostPeer = async viewerId => {
      if (!isHost || !localGameStreamRef.current || !active || !gameId) return;
      closePeer(viewerId);
      const ice = await turnServers();
      if (!active || !localGameStreamRef.current) return;
      const pc = new RTCPeerConnection({ iceServers: ice.iceServers || [] });
      peers.set(viewerId, pc);
      localGameStreamRef.current.getTracks().forEach(track => pc.addTrack(track, localGameStreamRef.current));
      pc.onicecandidate = ({ candidate }) => {
        if (candidate) socket.emit('game_stream_ice', { to: viewerId, gameId, candidate: candidate.toJSON?.() || candidate });
      };
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed'].includes(pc.connectionState)) closePeer(viewerId);
      };
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('game_stream_offer', { to: viewerId, gameId, offer: pc.localDescription?.sdp || offer.sdp });
      } catch {
        closePeer(viewerId);
      }
    };
    const onViewerJoined = ({ viewerId, gameId: requestedGameId }) => {
      if (requestedGameId === gameId) createHostPeer(viewerId);
    };
    const onOffer = async ({ from, gameId: offeredGameId, offer }) => {
      if (isHost || offeredGameId !== gameId || !offer) return;
      closePeer(from);
      const ice = await turnServers();
      if (!active) return;
      const pc = new RTCPeerConnection({ iceServers: ice.iceServers || [] });
      peers.set(from, pc);
      pc.ontrack = ({ streams, track }) => {
        const stream = streams?.[0] || new MediaStream([track]);
        setRemoteGameStream(stream);
        setEmulatorReady(true);
      };
      pc.onicecandidate = ({ candidate }) => {
        if (candidate) socket.emit('game_stream_ice', { to: from, gameId, candidate: candidate.toJSON?.() || candidate });
      };
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed'].includes(pc.connectionState)) {
          closePeer(from);
          setRemoteGameStream(null);
          if (active) window.setTimeout(() => socket.emit('game_stream_request', { gameId }), 1000);
        }
      };
      try {
        await pc.setRemoteDescription({ type: 'offer', sdp: offer });
        await drainIce(from);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('game_stream_answer', { to: from, gameId, answer: pc.localDescription?.sdp || answer.sdp });
      } catch {
        closePeer(from);
      }
    };
    const onAnswer = async ({ from, gameId: answeredGameId, answer }) => {
      if (!isHost || answeredGameId !== gameId || !answer) return;
      const pc = peers.get(from);
      if (!pc) return;
      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: answer });
        await drainIce(from);
      } catch { closePeer(from); }
    };
    const onIce = ({ from, gameId: candidateGameId, candidate }) => {
      if (candidateGameId === gameId) addIce(from, candidate);
    };
    const onReset = ({ gameId: resetGameId }) => {
      if (resetGameId !== gameId || isHost) return;
      closeAll();
      setRemoteGameStream(null);
      setEmulatorReady(false);
    };

    socket.on('game_stream_viewer_joined', onViewerJoined);
    socket.on('game_stream_offer', onOffer);
    socket.on('game_stream_answer', onAnswer);
    socket.on('game_stream_ice', onIce);
    socket.on('game_stream_reset', onReset);
    return () => {
      active = false;
      closeAll();
      if (!isHost) {
        setRemoteGameStream(null);
        setEmulatorReady(false);
      }
      socket.off('game_stream_viewer_joined', onViewerJoined);
      socket.off('game_stream_offer', onOffer);
      socket.off('game_stream_answer', onAnswer);
      socket.off('game_stream_ice', onIce);
      socket.off('game_stream_reset', onReset);
    };
  }, [socket, state.game?.gameId, isHost, localGameStream]);

  useEffect(() => {
    if (!socket || isHost || !state.game?.gameId || !state.streamReady) return;
    socket.emit('game_stream_request', { gameId: state.game.gameId });
  }, [socket, isHost, state.game?.gameId, state.streamReady]);

  useEffect(() => {
    if (!socket) return undefined;
    const onState = next => setState(next);
    const onInput = ({ gameId, controller, button, value }, done) => {
      if (!gameRef.current || gameRef.current.gameId !== gameId) {
        if (typeof done === 'function') done({ ok: false });
        return;
      }
      inputStats.current.received += 1;
      const nextValue = Number(value) || 0;
      pendingInputs.current.set(`${controller}:${button}`, nextValue);
      const simulate = window.EJS_emulator?.gameManager?.simulateInput;
      if (simulate) simulate(Number(controller) - 1, Number(button), nextValue);
      if (typeof done === 'function') done({ ok: true });
    };
    const onError = ({ error }) => setNotice(error || 'Game room action failed');
    const onDelivery = ({ inputId, deliveryMs }) => {
      if (!inputId || !Number.isFinite(Number(deliveryMs))) return;
      inputStats.current.playerDeliveryMs = Math.round(Number(deliveryMs));
    };
    let active = true;
    let probeInFlight = false;
    const probe = () => {
      if (!active || !socket.connected || probeInFlight) return;
      probeInFlight = true;
      const startedAt = performance.now();
      const manager = window.EJS_emulator?.gameManager;
      const frame = Number(manager?.getFrameNum?.());
      const frameAt = performance.now();
      let fps = frameStats.current.fps;
      if (Number.isFinite(frame) && Number.isFinite(frameStats.current.frame) && frameAt > frameStats.current.at) {
        fps = Math.max(0, Math.min(120, (frame - frameStats.current.frame) * 1000 / (frameAt - frameStats.current.at)));
      }
      if (Number.isFinite(frame)) frameStats.current = { frame, at: frameAt, fps };
      const audioContext = window.EJS_emulator?.Module?.AL?.currentCtx?.audioCtx;
      const audioState = audioContext?.state || 'unknown';
      const audioSampleRate = Number(audioContext?.sampleRate);
      socket.timeout(2500).emit('game_latency_probe', (error, result) => {
        probeInFlight = false;
        if (!active) return;
        const transport = socket.io?.engine?.transport?.name || (socket.connected ? 'connected' : 'disconnected');
        if (error || !result?.ok) {
          setNetworkStats(previous => ({ ...previous, transport }));
          return;
        }
        const rttMs = Math.round(performance.now() - startedAt);
        const resolvedTransport = socket.io?.engine?.transport?.name || result.transport || transport;
        const inputAckMs = inputStats.current.inputAckMs;
        setNetworkStats({
          rttMs,
          inputAckMs,
          transport: resolvedTransport,
          frame: Number.isFinite(frame) ? Math.round(frame) : null,
          fps: Number.isFinite(fps) ? Number(fps.toFixed(1)) : null,
          audioState,
          audioSampleRate: Number.isFinite(audioSampleRate) ? Math.round(audioSampleRate) : null,
          receivedInputs: inputStats.current.received,
          serverInputs: result.metrics?.acceptedInputs || 0,
          playerForwards: result.metrics?.playerForwards || 0,
          spectatorForwards: result.metrics?.spectatorForwards || 0,
          playerDeliveryMs: inputStats.current.playerDeliveryMs,
          playerDeliveryFailures: result.metrics?.playerDeliveryFailures || 0,
        });
        socket.emit('game_client_metrics', { rttMs, inputAckMs, playerDeliveryMs: inputStats.current.playerDeliveryMs, transport: resolvedTransport, frame, fps, audioState, audioSampleRate });
      });
    };
    const onConnect = () => {
      setNetworkStats(previous => ({ ...previous, transport: socket.io?.engine?.transport?.name || 'connected' }));
      socket.emit('game_join');
      probe();
    };
    const onDisconnect = () => {
      probeInFlight = false;
      setNetworkStats(previous => ({ ...previous, transport: 'disconnected' }));
    };
    const onConnectError = () => {
      probeInFlight = false;
      setNetworkStats(previous => ({ ...previous, transport: 'unavailable' }));
    };
    const probeInterval = window.setInterval(probe, 3000);
    socket.on('game_state', onState);
    socket.on('game_input', onInput);
    socket.on('game_input_delivery', onDelivery);
    socket.on('game_error', onError);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    if (socket.connected) onConnect();
    return () => {
      active = false;
      probeInFlight = false;
      window.clearInterval(probeInterval);
      const controller = seatRef.current;
      const game = gameRef.current;
      const pressed = new Set([...Object.values(sourceState.current)].flatMap(set => [...set]));
      pressed.forEach(button => {
        window.EJS_emulator?.gameManager?.simulateInput?.(Number(controller) - 1, button, 0);
        if (controller && game) socket.emit('game_input', { gameId: game.gameId, controller, button, pressed: false, value: 0 });
      });
      socket.emit('game_release_controller');
      socket.emit('game_leave');
      socket.off('game_state', onState);
      socket.off('game_input', onInput);
      socket.off('game_input_delivery', onDelivery);
      socket.off('game_error', onError);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };
  }, [socket, isMobile]);

  const applyInput = useCallback((controller, button, value) => {
    pendingInputs.current.set(`${controller}:${button}`, value);
    const simulate = window.EJS_emulator?.gameManager?.simulateInput;
    if (simulate) simulate(controller - 1, button, value);
  }, []);

  const resumeEmulatorAudio = useCallback(() => {
    const audioContext = window.EJS_emulator?.Module?.AL?.currentCtx?.audioCtx;
    if (!audioContext || audioContext.state !== 'suspended') return;
    try {
      const resume = audioContext.resume?.();
      resume?.catch?.(() => {});
    } catch {}
  }, []);

  const sendInput = useCallback((button, pressed, source = 'keyboard') => {
    const controller = seatRef.current;
    const game = gameRef.current;
    if (!controller || !game) return;
    const sources = sourceState.current;
    const wasPressed = [...Object.values(sources)].some(set => set.has(button));
    if (pressed) sources[source].add(button);
    else sources[source].delete(button);
    const isPressed = [...Object.values(sources)].some(set => set.has(button));
    if (wasPressed === isPressed) return;
    const value = isPressed ? 1 : 0;
    resumeEmulatorAudio();
    applyInput(controller, button, value);
    const inputSocket = socketRef.current;
    if (!inputSocket) return;
    inputStats.current.sent += 1;
    inputStats.current.nextId += 1;
    const inputId = `${Date.now().toString(36)}-${inputStats.current.nextId}`;
    const sentAt = performance.now();
    inputSocket.emit('game_input', { gameId: game.gameId, controller, button, pressed: isPressed, value, inputId }, ack => {
      if (ack?.ok) inputStats.current.inputAckMs = Math.round(performance.now() - sentAt);
    });
  }, [applyInput, resumeEmulatorAudio]);

  const clearSource = useCallback((source) => {
    [...sourceState.current[source]].forEach(button => sendInput(button, false, source));
    sourceState.current[source].clear();
  }, [sendInput]);

  useEffect(() => () => {
    clearSource('keyboard');
    clearSource('gamepad');
    clearSource('pointer');
    socketRef.current?.emit('game_release_controller');
    socketRef.current?.emit('game_leave');
  }, [clearSource]);

  useEffect(() => {
    const resumeAudio = () => resumeEmulatorAudio();
    window.addEventListener('pointerdown', resumeAudio, true);
    window.addEventListener('touchstart', resumeAudio, true);
    window.addEventListener('keydown', resumeAudio, true);
    return () => {
      window.removeEventListener('pointerdown', resumeAudio, true);
      window.removeEventListener('touchstart', resumeAudio, true);
      window.removeEventListener('keydown', resumeAudio, true);
    };
  }, [resumeEmulatorAudio]);

  useEffect(() => {
    const isEditable = target => target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT' || target?.isContentEditable;
    const keyDown = event => {
      if (isEditable(event.target) || !seatRef.current || !KEYMAPS[seatRef.current]) return;
      const button = KEYMAPS[seatRef.current][event.code];
      if (button === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      sendInput(button, true, 'keyboard');
    };
    const keyUp = event => {
      const button = KEYMAPS[seatRef.current]?.[event.code];
      if (button === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      sendInput(button, false, 'keyboard');
    };
    window.addEventListener('keydown', keyDown, true);
    window.addEventListener('keyup', keyUp, true);
    const blur = () => clearSource('keyboard');
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', keyDown, true);
      window.removeEventListener('keyup', keyUp, true);
      window.removeEventListener('blur', blur);
    };
  }, [clearSource, sendInput]);

  useEffect(() => {
    let frame;
    const poll = () => {
      const controller = seatRef.current;
      if (controller && navigator.getGamepads) {
        const pads = navigator.getGamepads();
        const pad = pads[controller - 1] || pads[0];
        if (pad) {
          const active = new Set();
          if (pad.buttons[12]?.pressed) active.add(4);
          if (pad.buttons[13]?.pressed) active.add(5);
          if (pad.buttons[14]?.pressed) active.add(6);
          if (pad.buttons[15]?.pressed) active.add(7);
          [[0, 8], [1, 0], [2, 9], [3, 1], [4, 10], [5, 11], [8, 2], [9, 3]].forEach(([index, button]) => {
            if (pad.buttons[index]?.pressed) active.add(button);
          });
          if ((pad.axes[1] || 0) < -0.45) active.add(4);
          if ((pad.axes[1] || 0) > 0.45) active.add(5);
          if ((pad.axes[0] || 0) < -0.45) active.add(6);
          if ((pad.axes[0] || 0) > 0.45) active.add(7);
          const old = sourceState.current.gamepad;
          [...old].filter(button => !active.has(button)).forEach(button => sendInput(button, false, 'gamepad'));
          [...active].filter(button => !old.has(button)).forEach(button => sendInput(button, true, 'gamepad'));
        } else {
          [...sourceState.current.gamepad].forEach(button => sendInput(button, false, 'gamepad'));
        }
      }
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
  }, [sendInput]);

  useEffect(() => {
    pendingInputs.current.clear();
    setEmulatorError('');
  }, [state.game?.gameId]);

  useEffect(() => {
    if (!socket || !state.game) return undefined;
    socket.emit('game_client_status', { gameId: state.game.gameId, ready: emulatorReady });
    return () => socket.emit('game_client_status', { gameId: state.game.gameId, ready: false });
  }, [socket, state.game?.gameId, emulatorReady]);

  const chooseController = controller => {
    setNotice('');
    socket?.emit('game_claim_controller', { controller });
  };

  const assignController = (controller, socketId) => {
    socket?.emit('game_assign_controller', { controller, socketId: socketId || null });
  };

  const releaseController = () => {
    clearSource('keyboard');
    clearSource('gamepad');
    clearSource('pointer');
    socket?.emit('game_release_controller');
  };

  const uploadRom = async file => {
    if (!file || !socket) return;
    if (file.size > 32 * 1024 * 1024) {
      setNotice('That ROM is larger than the 32 MB upload limit.');
      return;
    }
    let detectedSystem;
    try {
      detectedSystem = await detectRomSystem(file);
      setSystem(detectedSystem);
    } catch (error) {
      setNotice(error.message || 'I could not identify that ROM.');
      return;
    }
    setUploading(true);
    const abortController = new AbortController();
    const timeout = window.setTimeout(() => abortController.abort(), 60000);
    setNotice('Uploading ROM to the game room…');
    try {
      const response = await fetch(`${SERVER}/stream/game/rom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'X-Game-System': detectedSystem, 'X-Game-Name': file.name },
        body: file,
        signal: abortController.signal,
      });
      const contentType = response.headers.get('content-type') || '';
      const responseText = await response.text();
      let result = {};
      if (contentType.includes('application/json')) {
        try { result = JSON.parse(responseText); } catch { result = {}; }
      }
      if (!response.ok && response.status === 404 && /Cannot POST \/stream\/game\/rom/i.test(responseText)) {
        throw new Error('The live server has not been updated with game uploads yet. Deploy and restart the latest tavern-server, then try again.');
      }
      if (!response.ok) throw new Error(result.error || `ROM upload failed (HTTP ${response.status})`);
      setNotice('ROM uploaded. Starting the emulator for everyone...');
      socket.emit('game_set_game', { romId: result.romId, system: detectedSystem, name: result.name }, ack => {
        if (ack && !ack.ok) setNotice(ack.error || 'The server rejected this ROM.');
      });
    } catch (error) {
      setNotice(error.name === 'AbortError' ? 'ROM upload timed out. Check the server connection and try again.' : (error.message || 'ROM upload failed'));
    } finally {
      window.clearTimeout(timeout);
      setUploading(false);
    }
  };

  const getStateManager = () => {
    const manager = window.EJS_emulator?.gameManager;
    if (!emulatorReady || !manager || (typeof manager.supportsStates === 'function' && !manager.supportsStates())) {
      setNotice('Save states are not available until the emulator is ready.');
      return null;
    }
    return manager;
  };

  const quickSave = () => {
    const manager = getStateManager();
    if (!manager) return;
    try {
      const saved = manager.quickSave?.('1');
      setNotice(saved === false ? 'Could not save the game state.' : 'Quick save stored in this browser (slot 1).');
    } catch (error) {
      setNotice(error.message || 'Could not save the game state.');
    }
  };

  const quickLoad = () => {
    const manager = getStateManager();
    if (!manager) return;
    try {
      manager.quickLoad?.('1');
      setNotice('Quick save loaded from this browser (slot 1).');
    } catch (error) {
      setNotice(error.message || 'Could not load the game state.');
    }
  };

  const exportState = () => {
    const manager = getStateManager();
    if (!manager) return;
    try {
      const stateBytes = manager.getState();
      const blob = new Blob([stateBytes], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = String(state.game?.name || 'tavern-game').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || 'tavern-game';
      link.href = url;
      link.download = `${safeName}.state`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice('State file downloaded.');
    } catch (error) {
      setNotice(error.message || 'Could not export the game state.');
    }
  };

  const importState = async file => {
    if (!file) return;
    const manager = getStateManager();
    if (!manager) return;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      manager.loadState(bytes);
      setNotice('State file loaded.');
    } catch (error) {
      setNotice(error.message || 'Could not load that state file.');
    }
  };

  const refreshRoomGame = () => {
    if (!game || !socket || !isHost) return;
    clearSource('keyboard');
    clearSource('gamepad');
    clearSource('pointer');
    emulatorDestroyRef.current?.();
    setEmulatorReady(false);
    setNotice('Refreshing the game for everyone…');
    socket.timeout(5000).emit('game_refresh', (error, result) => {
      if (error) setNotice('The game refresh timed out. Check the room connection and try again.');
      else if (!result?.ok) setNotice(result?.error || 'The host could not refresh the game.');
      else setNotice('Game refreshed for everyone in the room.');
    });
  };

  const gameError = emulatorError || notice;
  const game = state.game;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, background: C.bg, minHeight: '100%', boxSizing: 'border-box', minWidth: 0, overflowX: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 19, fontWeight: 800, color: C.text }}>🎮 Play Together</div>
        <span style={{ fontSize: 12, color: C.muted }}>NES · SNES · Genesis</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: C.muted }}>{state.members.length} in game room</span>
      </div>

      {gameError && <div style={{ background: `${C.gold}18`, border: `1px solid ${C.gold}55`, color: C.gold, padding: '9px 12px', borderRadius: 9, fontSize: 13 }}>{gameError}</div>}

      {!game ? (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 750, color: C.text }}>Start a shared game</div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>Any participant can load a ROM once. Everyone in this room receives the same game and can spectate or claim one of two controller seats.</div>
          </div>
          {canLoadGame ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={system} onChange={e => setSystem(e.target.value)} style={{ background: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                {Object.entries(SYSTEMS).map(([id, item]) => <option key={id} value={id}>{item.label}</option>)}
              </select>
              <label style={{ background: C.gold, color: '#0E0E10', borderRadius: 8, padding: '10px 15px', fontSize: 13, fontWeight: 800, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                {uploading ? 'Uploading…' : 'Choose ROM'}
                <input type="file" accept={ROM_ACCEPT} disabled={uploading} onChange={e => { uploadRom(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} />
              </label>
              <span style={{ color: C.muted, fontSize: 11 }}>The console is detected from the ROM or ZIP contents.</span>
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: 13 }}>Waiting for {state.members.find(member => member.socketId === state.hostId)?.name || 'the host'} to load a ROM…</div>
          )}
          <div style={{ color: C.muted, fontSize: 11 }}>Anyone in this room can load a game. Upload games you own or have permission to share; ROM files are held in server memory for the active session.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px 10px 0 0', padding: '10px 13px', marginBottom: -12 }}>
            <span style={{ fontWeight: 800, color: C.text }}>{game.name}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{SYSTEMS[game.system]?.label || game.system}</span>
            <span style={{ fontSize: 11, color: emulatorReady ? C.green : C.muted }}>{emulatorReady ? '● Ready' : '○ Loading'}</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setDiagnosticsOpen(open => !open)} style={{ background: diagnosticsOpen ? `${C.gold}22` : 'transparent', color: diagnosticsOpen ? C.gold : C.muted, border: `1px solid ${diagnosticsOpen ? C.gold : C.border}`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 }}>
              Network {networkStats.rttMs == null ? '—' : `${networkStats.rttMs} ms`}
            </button>
            <button disabled={!emulatorReady} onClick={quickSave} style={{ background: 'transparent', color: emulatorReady ? C.text : C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px', cursor: emulatorReady ? 'pointer' : 'not-allowed', fontSize: 11 }}>Quick save</button>
            <button disabled={!emulatorReady} onClick={quickLoad} style={{ background: 'transparent', color: emulatorReady ? C.text : C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px', cursor: emulatorReady ? 'pointer' : 'not-allowed', fontSize: 11 }}>Quick load</button>
            <button disabled={!emulatorReady} onClick={exportState} style={{ background: 'transparent', color: emulatorReady ? C.text : C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px', cursor: emulatorReady ? 'pointer' : 'not-allowed', fontSize: 11 }}>Export state</button>
            <label style={{ background: 'transparent', color: emulatorReady ? C.text : C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px', cursor: emulatorReady ? 'pointer' : 'not-allowed', fontSize: 11, opacity: emulatorReady ? 1 : 0.55 }}>
              Import state
              <input type="file" accept=".state,application/octet-stream" disabled={!emulatorReady} onChange={e => { importState(e.target.files?.[0]); e.target.value = ''; }} style={{ display: 'none' }} />
            </label>
            {isHost && <button onClick={refreshRoomGame} style={{ background: 'transparent', color: C.gold, border: `1px solid ${C.gold}88`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 }} title="Reload the emulator for everyone in the room">Refresh game</button>}
            {canLoadGame && <button onClick={() => { emulatorDestroyRef.current?.(); socket?.emit('game_clear'); }} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 }}>End game</button>}
          </div>
          {diagnosticsOpen && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 9, padding: '8px 10px', color: C.muted, fontSize: 11 }}>
            <span>Transport: <strong style={{ color: C.text }}>{networkStats.transport}</strong></span>
            <span>Playback: <strong style={{ color: C.text }}>{isHost ? 'Host emulator' : remoteGameStream ? 'WebRTC host stream' : 'Waiting for host'}</strong></span>
            <span>Emulator FPS: <strong style={{ color: C.text }}>{networkStats.fps == null ? 'â€”' : networkStats.fps}</strong></span>
            <span>Audio: <strong style={{ color: C.text }}>{networkStats.audioState}{networkStats.audioSampleRate ? ` · ${networkStats.audioSampleRate} Hz` : ''}</strong></span>
            <span>Server RTT: <strong style={{ color: C.text }}>{networkStats.rttMs == null ? '—' : `${networkStats.rttMs} ms`}</strong></span>
            <span>Input ACK: <strong style={{ color: C.text }}>{networkStats.inputAckMs == null ? '—' : `${networkStats.inputAckMs} ms`}</strong></span>
            <span>Player delivery: <strong style={{ color: C.text }}>{networkStats.playerDeliveryMs == null ? '—' : `${networkStats.playerDeliveryMs} ms`}</strong></span>
            <span>Inputs received: <strong style={{ color: C.text }}>{networkStats.receivedInputs}</strong></span>
            <span>Relay: <strong style={{ color: C.text }}>{networkStats.playerForwards} players / {networkStats.spectatorForwards} spectators</strong></span>
            {networkStats.playerDeliveryFailures > 0 && <span style={{ color: C.red }}>Player delivery timeouts: {networkStats.playerDeliveryFailures}</span>}
            <span style={{ flexBasis: '100%', color: C.muted }}>Local player input is applied immediately; the server sends player updates before spectator updates.</span>
          </div>}
          {isHost ? (
            <GameCanvas game={game} pendingInputs={pendingInputs} onReady={handleCanvasReady} onError={setEmulatorError} onStreamReady={handleLocalGameStream} isMobile={isMobile} destroyRef={emulatorDestroyRef} captureStream />
          ) : (
            <GameVideo stream={remoteGameStream} onReady={setEmulatorReady} />
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {controllerSeats.map(seat => {
              const mine = seat.socketId === myMember?.socketId;
              return (
                <div key={seat.controller} style={{ flex: '1 1 220px', background: C.panel, border: `1px solid ${mine ? (seat.controller === 1 ? C.accent : C.green) : C.border}`, borderRadius: 10, padding: 11, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
                  <div style={{ width: 30, height: 30, display: 'grid', placeItems: 'center', borderRadius: 8, background: seat.controller === 1 ? `${C.accent}22` : `${C.green}22`, color: seat.controller === 1 ? C.accent : C.green, fontWeight: 900 }}>P{seat.controller}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{seat.name ? `${seat.name}${mine ? ' (you)' : ''}` : 'Open controller'}</div>
                    <div style={{ color: C.muted, fontSize: 11 }}>{mine ? (emulatorReady ? 'Ready · keyboard, gamepad, or touch' : 'Loading emulator…') : seat.name ? (seat.ready ? 'Connected and ready' : 'Emulator loading') : 'Claim to play'}</div>
                  </div>
                  {isHost && <select aria-label={`Assign player ${seat.controller}`} value={seat.socketId || ''} onChange={e => assignController(seat.controller, e.target.value)} style={{ maxWidth: 150, background: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 7px', fontSize: 11 }}>
                    <option value="">Open seat</option>
                    {state.members.map(member => <option key={member.socketId} value={member.socketId}>{member.name}{member.socketId === socket?.id ? ' (you)' : ''}</option>)}
                  </select>}
                  {!seat.name && !mySeat && <button onClick={() => chooseController(seat.controller)} style={{ background: C.gold, color: '#0E0E10', border: 'none', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontWeight: 800, fontSize: 11 }}>Play</button>}
                  {mine && <button onClick={releaseController} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 9px', cursor: 'pointer', fontSize: 11 }}>Leave</button>}
                </div>
              );
            })}
          </div>

          {mySeat ? (
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ color: C.muted, fontSize: 12 }}>P{mySeat}: arrows + Z/X/A/S, or connect a gamepad. Start is Enter.</div>
              <ControllerPad controller={mySeat} active={emulatorReady} onPress={(button, pressed) => sendInput(button, pressed, 'pointer')} />
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: 12, padding: '2px 2px 6px' }}>You’re spectating. Claim an open controller above to play.</div>
          )}
        </>
      )}
    </div>
  );
}
